import { defineStore } from 'pinia';

import { FieldMeta } from '@/modules/dynamic-form/models/FieldMeta.model';
import { FieldPath } from '@/modules/dynamic-form/models/FieldPath.model';
import { FieldValidator } from '@/modules/dynamic-form/models/FieldValidator.model';
import { UnifiedFieldRecordKey, UnifiedFieldRecord } from '@/modules/dynamic-form/models/UnifiedFieldRecord.model';
import { ValidationRules } from '@/modules/dynamic-form/models/ValidationRuleNames.model';
import { ComponentResolver } from '@/modules/dynamic-form/utils/get-control-components/getControlComponents';

export interface StoreFormInstance {
    showErrors: boolean;
    rbacDisabled: boolean;
    values: Record<FieldPath, any>;
    fieldsMeta: Record<string, FieldMeta>;
    formErrors: UnifiedFieldRecord<string>;
    disabledFields: UnifiedFieldRecord<boolean>;
    dirtyFields: UnifiedFieldRecord<boolean>;
    fieldValidators: UnifiedFieldRecord<FieldValidator[]>;
    customControls?: Record<string, ComponentResolver>;
    customMessages?: Record<ValidationRules, string>;
}

export interface DynamicFormState {
    forms: Record<string, StoreFormInstance>;
}

// The legacy vuex module was pure wrapper-actions over same-named mutations;
// each pair collapses into one Pinia action keeping the wrapper's public
// signature (and its input defaulting) with the mutation's body.
export const useDynamicFormStore = defineStore('dynamicForm', {
    state: (): DynamicFormState => ({
        forms: {},
    }),
    actions: {
        initializeForm(payload: {
            formId: string;
            values: any;
            fieldsMeta: Record<FieldPath, FieldMeta>;
            customControls?: Record<FieldPath, ComponentResolver>;
            customMessages?: Record<ValidationRules, string>;
            fieldValidators: UnifiedFieldRecord<FieldValidator[]>;
            disabledFields?: UnifiedFieldRecord<boolean>;
            dirtyFields?: UnifiedFieldRecord<boolean>;
        }) {
            const existingForm = this.forms[payload.formId];
            this.forms = {
                ...this.forms,
                [payload.formId]: {
                    showErrors: existingForm?.showErrors ?? false,
                    rbacDisabled: existingForm?.rbacDisabled ?? false,
                    values: payload.values || {},
                    fieldsMeta: payload.fieldsMeta || {},
                    customControls: payload.customControls || {},
                    customMessages: payload.customMessages || ({} as Record<ValidationRules, string>),
                    fieldValidators: payload.fieldValidators || {},
                    disabledFields: payload.disabledFields || {},
                    dirtyFields: payload.dirtyFields || {},
                    formErrors: {},
                },
            };
        },
        cleanupForm(payload: { formId: string }) {
            const { [payload.formId]: _removed, ...remainingForms } = this.forms;
            this.forms = remainingForms;
        },
        setValueByPath(payload: { formId: string, path: string, value: any }) {
            const form = this.forms[payload.formId];
            if (!form) {
                return;
            }
            form.values = { ...form.values, [payload.path]: payload.value };
        },
        setValues(payload: { formId: string, values: Record<FieldPath, any> }) {
            const form = this.forms[payload.formId];
            if (!form) {
                return;
            }
            form.values = { ...form.values, ...payload.values };
        },
        setFieldError(payload: { formId: string, path: string, error: Record<UnifiedFieldRecordKey, string> }) {
            const form = this.forms[payload.formId];
            if (!form) {
                return;
            }
            form.formErrors = { ...form.formErrors, [payload.path]: payload.error };
        },
        deleteFieldError(payload: { formId: string, path: string }) {
            const form = this.forms[payload.formId];
            if (!form) {
                return;
            }
            const { [payload.path as FieldPath]: _removedError, ...remainingErrors } = form.formErrors;
            form.formErrors = remainingErrors;
        },
        setShowErrors(payload: { formId: string, showError: boolean }) {
            const form = this.forms[payload.formId];
            if (!form) {
                return;
            }
            form.showErrors = payload.showError;
        },
        setShowErrorsMultiple(payload: { formIds: string[], showError: boolean }) {
            payload.formIds.forEach((formId) => {
                const form = this.forms[formId];
                if (form) {
                    form.showErrors = payload.showError;
                }
            });
        },
        setDisabledFieldByPath(
            payload: { formId: string, path: string, value: Record<UnifiedFieldRecordKey, boolean> },
        ) {
            const form = this.forms[payload.formId];
            if (!form) {
                return;
            }
            form.disabledFields = { ...(form.disabledFields ?? {}), [payload.path]: payload.value };
        },
        setDisabledFields(payload: { formId: string, disabledFields: UnifiedFieldRecord<boolean> }) {
            const form = this.forms[payload.formId];
            if (!form) {
                return;
            }
            form.disabledFields = { ...(form.disabledFields ?? {}), ...payload.disabledFields };
        },
        setDirtyFieldByPath(payload: { formId: string, path: string, value: Record<UnifiedFieldRecordKey, boolean> }) {
            const form = this.forms[payload.formId];
            if (!form) {
                return;
            }
            form.dirtyFields = { ...(form.dirtyFields ?? {}), [payload.path]: payload.value };
        },
    },
});
